import React, { useState, useEffect, useMemo } from 'react';
import { dashboardStats } from '../../api/services/dashboard-stats/index'
import Popup from 'reactjs-popup';
import DatePicker from 'react-date-picker';
import { HalfCircleSpinner } from 'react-epic-spinners';
import { useAuth } from '../../api/services/auth';
import { userPermissions } from '../../enums';
import { StatsCardGrid } from './StatsCardGrid';

function displayValue(value) {
    if (value == null || value === '') {
        return '—';
    }
    return value;
}

function HodStats() {
    const [statsData, setStatsData] = useState([]);
    const [showLoader, setShowLoader] = useState(true);
    const [filterType, setFilterType] = useState('this-week')
    const [showDatePicker, setShowDatePicker] = useState(false);
    const [fromDate, setFromDate] = useState('')
    const [toDate, setToDate] = useState('')
    const [errors, setErrors] = useState({});
    const { permission } = useAuth();

    const canListCases = permission?.some((p) => p.name === userPermissions.CASES_LIST);
    const canListUsers = permission?.some((p) => p.name === userPermissions.USERS_LIST);
    const stats = statsData[0] || {};

    // Get_Dashboard_Stats_Data
    useEffect(() => {
        dashboardStats.get('this-week')
            .then((res) => {
                setShowLoader(false);
                setStatsData(res?.data);
            }).catch(error => {
                setShowLoader(false);
                console.error('Error fetching data:', error);
            });
    }, []);

    const checkValidation = () => {
        let nextErrors = {};
        let isValid = true;

        if (!fromDate) {
            isValid = false;
            nextErrors['fromDate'] = 'Please enter from Date.';
        }
        if (!toDate) {
            isValid = false;
            nextErrors['toDate'] = 'Please select to Date.';
        }
        setErrors(nextErrors);
        return isValid;
    };

    const handleFilter = (e) => {
        const filter = e.target.value;
        setFilterType(filter)
        if (filter !== 'custom') {
            setShowLoader(true);

            dashboardStats.get(filter)
                .then((res) => {
                    setShowLoader(false);
                    setStatsData(res?.data);
                }).catch(error => {
                    setShowLoader(false);
                    console.error('Error fetching data:', error);
                });
        } else if (filter === 'custom') {
            setShowDatePicker(true)
            e.target.value = null
        }
    }

    const handleCustomSubmit = () => {
        if (checkValidation()) {
            setShowDatePicker(false)
        }
    }

    const handleFromDate = (newDate) => {
        if (newDate instanceof Date && !isNaN(newDate)) {
            const year = newDate.getFullYear();
            const month = String(newDate.getMonth() + 1).padStart(2, '0');
            const day = String(newDate.getDate()).padStart(2, '0');
            setFromDate(`${year}-${month}-${day}`);
        } else {
            setFromDate(null);
        }
    };

    const handleToDate = (newDate) => {
        if (newDate instanceof Date && !isNaN(newDate)) {
            const year = newDate.getFullYear();
            const month = String(newDate.getMonth() + 1).padStart(2, '0');
            const day = String(newDate.getDate()).padStart(2, '0');
            setToDate(`${year}-${month}-${day}`);
        } else {
            setToDate(null);
        }
    };

    const overview = useMemo(() => {
        const sections = [];

        if (canListCases) {
            sections.push({
                title: 'Department',
                subtitle: 'Case workload',
                cards: [
                    { label: 'Flagged', value: displayValue(stats.flag_cases), icon: 'fa-solid fa-flag' },
                    { label: 'In discussion', value: displayValue(stats.cases_for_discussion) },
                    { label: 'Reviewed', value: displayValue(stats.reviewed) },
                    { label: 'Unreviewed', value: displayValue(stats.un_reviewed) },
                    { label: 'With comments', value: displayValue(stats.comments_count) },
                    { label: 'No comments', value: displayValue(stats.with_no_comments_count) },
                ],
            });
        }

        if (canListUsers) {
            sections.push({
                title: 'Team',
                subtitle: 'User activity',
                cards: [
                    { label: 'Active users', value: displayValue(stats.active_users) },
                    { label: 'Total users', value: displayValue(stats.in_active_users) },
                ],
            });
        }

        return { sections };
    }, [canListCases, canListUsers, stats]);

    return (
        <>
            <div className="statsWidget statsChartWidget colSpan100">
                <article className="widgetWrap">
                    <div className="widgetHeader">
                        <span>All Department Stats</span>
                        <div className="selectWrap posRelative caseFilters">
                            <select
                                onChange={handleFilter}
                                name="cases"
                                className="widgetSelect"
                                id="all-cases"
                                value={filterType === 'custom' ? '' : filterType}
                                aria-label="Stats period"
                            >
                                <option value="this-week">This Week</option>
                                <option value="this-month">This Month</option>
                                <option value="last-week">Last Week</option>
                                <option value="last-month">Last Month</option>
                            </select>
                        </div>
                    </div>
                    {showLoader ? (
                        <div className='textCenter loadingState' style={{ minHeight: '180px' }}>
                            <HalfCircleSpinner
                                size={18}
                                color='black'
                                style={{
                                    display: "inline-block",
                                    marginLeft: "7px",
                                    position: "relative",
                                    top: "2px",
                                }}
                            />
                        </div>
                    ) : (
                        <div className='widgetBody'>
                            <StatsCardGrid sections={overview.sections} />
                        </div>
                    )}
                </article>
            </div>

            <Popup
                className="deleteUserModal"
                open={showDatePicker}
                onClose={() => {
                    setShowDatePicker(false)
                }}
            >
                {showDatePicker && (
                    <div className="modal textCenter">
                        <div>
                            <p>From</p>
                            <DatePicker
                                className={`formControl datePickerInput ${errors.fromDate && 'error'}`}
                                onChange={handleFromDate}
                                value={fromDate ? new Date(fromDate) : null}
                                dayPlaceholder='dd'
                                monthPlaceholder='mm'
                                yearPlaceholder='yyyy'
                                format='y/MM/dd'
                                maxDate={new Date(2099, 11, 31)}
                            />
                        </div>
                        <div>
                            <p>To</p>
                            <DatePicker
                                className={`formControl datePickerInput ${errors.toDate && 'error'}`}
                                onChange={handleToDate}
                                value={toDate ? new Date(toDate) : null}
                                dayPlaceholder='dd'
                                monthPlaceholder='mm'
                                yearPlaceholder='yyyy'
                                format='y/MM/dd'
                                maxDate={new Date(2099, 11, 31)}
                            />
                        </div>
                        <div className="btnWrap">
                            <button
                                onClick={() => {
                                    setShowDatePicker(false)
                                    setFromDate('')
                                    setToDate('')
                                }}
                                type="button"
                                className="btn btnSecondary"
                            >Cancel</button>
                            <button
                                onClick={handleCustomSubmit}
                                type="button"
                                className="btn btnPrimary"
                            >Submit </button>
                        </div>
                    </div>
                )}
            </Popup>
        </>
    )
}

export default HodStats
